///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, OnDestroy } from '@angular/core';
import { FREQUENCY_SET_AUTOMATICALLY, LinkFeature } from '../../entities/link/link-feature';
import { Observable, Subject } from 'rxjs';
import { GtthRemoveLinkCommand } from '../../engine/commands/link/remove-link-command';
import { GtthHistoryService } from '../../engine/history-service';
import { GtthModifyLinkInfoCommand } from '../../engine/commands/link/modify-link-info-command';
import { TopologyLinkTypeEnum } from '../../../../services/project-socket/dto/topology-types.model';
import { LinkMarks } from '../../entities/link/link-marks';
import { LinkProductsPairModel } from './link-panel/link-products-pair.model';
import { isDefined } from '@siklu/sui-lib/utils';
import { EngineAntennaDTO, PdbFddOffsetEnum } from '../../../../generated/shared-types.model';
import { ProjectProductsService } from '../../engine/project-products-service';
import { FrequencyOption } from './link-panel/link-info-panel.component';
import { GtthChangeFrequenciesMultipleLinksCommand } from '../../engine/commands/link/change-frequencies-multiple-links-command';
import { GtthFreezeSelectedLinksCommand } from '../../engine/commands/link/freeze-selected-links-command';
import { GtthChangeLinkNodesDataCommand } from '../../engine/commands/link/change-link-nodes-data-command';

@Injectable()
export class LinkInfoStateService implements OnDestroy {
    private link: LinkFeature;
    private initialProductsPair: LinkProductsPairModel;
    private selectedProductsPair: LinkProductsPairModel;
    private selectedFrequency: FrequencyOption;
    private modifyInfoCommand: GtthModifyLinkInfoCommand;
    private modifyLinkNodesDataCommand: GtthChangeLinkNodesDataCommand;

    private readonly close$ = new Subject<boolean>();

    constructor(
        private readonly historyService: GtthHistoryService,
        private readonly projectProductsService: ProjectProductsService
    ) {}

    setLink(link: LinkFeature): void {
        this.link = link;
        this.initialProductsPair = LinkProductsPairModel.createForLink(
            this.link,
            this.projectProductsService.getBaseRadioById(this.link.getUsedProd0()),
            this.projectProductsService.getBaseRadioById(this.link.getUsedProd1())
        );
        this.selectedProductsPair = this.initialProductsPair;

        this.modifyInfoCommand = this.historyService.command(GtthModifyLinkInfoCommand).create(this.link.getId());

        this.modifyInfoCommand.setOldUsedProd0(this.link.getUsedProd0());
        this.modifyInfoCommand.setOldUsedAnt0(this.link.getUsedAnt0());
        this.modifyInfoCommand.setOldUsedProd1(this.link.getUsedProd1());
        this.modifyInfoCommand.setOldUsedAnt1(this.link.getUsedAnt1());

        this.modifyInfoCommand.setOldLinkIsDisable(this.link.getLinkIsDisabled());
        this.modifyInfoCommand.setOldLinkType(this.link.getLinkType());

        this.modifyInfoCommand.setOldIsUserForced(this.link.isUserForced());
        this.modifyInfoCommand.setOldIsInstalled(this.link.isInstalled());
    }

    getModifyLinkNodesDataCommand(): GtthChangeLinkNodesDataCommand {
        if (!this.modifyLinkNodesDataCommand) {
            this.modifyLinkNodesDataCommand = this.historyService.command(GtthChangeLinkNodesDataCommand).create();
        }

        return this.modifyLinkNodesDataCommand;
    }

    getLink(): LinkFeature {
        return this.link;
    }

    subscribeToCloseEvent(): Observable<boolean> {
        return this.close$.asObservable();
    }

    ngOnDestroy(): void {
        this.destroyHandler().then();
    }

    async destroyHandler(): Promise<void> {
        if (this.modifyLinkNodesDataCommand) {
            await this.historyService.doCommand(this.modifyLinkNodesDataCommand);
        }

        if (this.modifyInfoCommand) {
            await this.historyService.doCommand(this.modifyInfoCommand);
            if (isDefined(this.selectedFrequency) && !this.modifyInfoCommand.linkWasUnfrozen()) {
                await this.checkOrModifyFrequency();
            }
        }

        if (this.historyService.bomReportService.isBomExist()) {
            GtthFreezeSelectedLinksCommand.applyLinkFreezeStateToBomDevices(this.link);
        }

        this.close$.complete();
    }

    changeLosType(losType: TopologyLinkTypeEnum): void {
        if (!this.link.isUserForced()) {
            this.modifyInfoCommand.setNewLinkType(losType);
        }
    }

    setLosDisabled(isDisabled: boolean): void {
        this.modifyInfoCommand.setNewLinkIsDisable(isDisabled);
    }

    async deleteLink(): Promise<void> {
        this.modifyInfoCommand = undefined;

        const removeLinkCommand = this.historyService.command(GtthRemoveLinkCommand).createFromId(this.link.getId());
        await this.historyService.doCommand(removeLinkCommand);
        this.close$.next(true);
    }

    getLinkState(): LinkMarks {
        let linkCurrentState = LinkMarks.LINK;
        if (this.link.isUserForced()) {
            linkCurrentState = this.link.isInstalled() ? LinkMarks.INSTALLED : LinkMarks.FROZEN;
        }
        return linkCurrentState;
    }

    setLinkState(value: LinkMarks): void {
        this.modifyInfoCommand.setNewIsUserForced(value === LinkMarks.FROZEN || value === LinkMarks.INSTALLED);
        this.modifyInfoCommand.setNewIsInstalled(value === LinkMarks.INSTALLED);
    }

    getAvailableProductPairs(): LinkProductsPairModel[] {
        const layerProducts = this.historyService.logicalTopology.selectedProducts[this.link.getLinkLayerIndex()]
            .filter((selectedProduct) => isDefined(selectedProduct.usedProd) || selectedProduct.threshold >= this.link.getDistance())
            .map((selectedProduct) =>
                this.selectedProductsPair.checkAndCreateTransitionPair(
                    this.projectProductsService.getBaseRadioById(selectedProduct.usedProd),
                    selectedProduct.usedAnt0,
                    this.projectProductsService.getBaseRadioById(selectedProduct.usedProdTU),
                    selectedProduct.usedAnt1
                )
            )
            .filter((pair) => isDefined(pair))
            .filter(
                (pair) =>
                    !pair.extendedByT280DnLink ||
                    !this.historyService.logicalTopology.autodesignP2mpPerLayer[this.link.getLinkLayerIndex()].isResiliencyEnabled()
            );

        const uniquePairs = new Map<string, LinkProductsPairModel>();
        const availablePairs = [];

        for (const productsPair of [this.selectedProductsPair, ...layerProducts]) {
            if (!uniquePairs.has(productsPair.title)) {
                availablePairs.push(productsPair);
                uniquePairs.set(productsPair.title, productsPair);
            } else if (productsPair.compliantProduct.isTU) {
                uniquePairs.get(productsPair.title).addSupportedTuAntenna(productsPair.compliantAntenna);
            }
        }

        return availablePairs;
    }

    getSelectedProductsPair(): LinkProductsPairModel {
        return this.selectedProductsPair;
    }

    changeRadioPair(productPair: LinkProductsPairModel): void {
        this.selectedProductsPair = productPair;

        this.modifyInfoCommand.setNewUsedProd0(this.selectedProductsPair.getProductByLocationName(this.link.getFromStation().getName()).id);
        this.modifyInfoCommand.setNewUsedProd1(this.selectedProductsPair.getProductByLocationName(this.link.getToStation().getName()).id);
    }

    linkRadiosTypeChanged(): boolean {
        return !this.initialProductsPair.productsEqual(this.selectedProductsPair);
    }

    getFromAvailableAntennas(): EngineAntennaDTO[] {
        return this.selectedProductsPair
            .getAvailableAntennasForLocation(this.link.getFromStation().getName())
            .sort((antenna1, antenna2) => antenna1.id - antenna2.id);
    }

    getFromAntenna(): EngineAntennaDTO {
        const availableAntennas = this.getFromAvailableAntennas();
        const antenna = availableAntennas.find((antenna) => antenna.id === this.link.getUsedAnt0());
        return antenna || this.selectedProductsPair.getDefaultAntennaByLocationName(this.link.getFromStation().getName());
    }

    changeFromAntenna(antenna: EngineAntennaDTO): void {
        this.modifyInfoCommand.setNewUsedAnt0(antenna.id);
    }

    getToAvailableAntennas(): EngineAntennaDTO[] {
        return this.selectedProductsPair
            .getAvailableAntennasForLocation(this.link.getToStation().getName())
            .sort((antenna1, antenna2) => antenna1.id - antenna2.id);
    }

    getToAntenna(): EngineAntennaDTO {
        const availableAntennas = this.getToAvailableAntennas();
        const antenna = availableAntennas.find((antenna) => antenna.id === this.link.getUsedAnt1());
        return antenna || this.selectedProductsPair.getDefaultAntennaByLocationName(this.link.getToStation().getName());
    }

    changeToAntenna(antenna: EngineAntennaDTO): void {
        this.modifyInfoCommand.setNewUsedAnt1(antenna.id);
    }

    getAvailableFrequencies(): FrequencyOption[] {
        const frequencies = this.selectedProductsPair.getSupportedFrequencies().map((value) => {
            return {
                title: '' + value,
                value: value,
                autoOption: false
            } as FrequencyOption;
        });

        let autoFrequency =
            this.link.getTxFrequency() < this.link.getRxFrequency() ? this.link.getTxFrequency() : this.link.getRxFrequency();
        if (this.linkRadiosTypeChanged()) {
            autoFrequency = FREQUENCY_SET_AUTOMATICALLY;
        }

        const autoFrequencyTitle =
            this.link.isFrequencyForced() || autoFrequency === FREQUENCY_SET_AUTOMATICALLY ? 'Auto' : 'Auto (' + autoFrequency + ')';
        frequencies.unshift({ value: autoFrequency, title: autoFrequencyTitle, autoOption: true });

        return frequencies;
    }

    getLinkFrequency(): FrequencyOption {
        const available = this.getAvailableFrequencies();
        const linkFrequency =
            this.link.getTxFrequency() < this.link.getRxFrequency() ? this.link.getTxFrequency() : this.link.getRxFrequency();
        const selectedOption = available.find(
            (frequency) => frequency.value === linkFrequency && frequency.autoOption !== this.link.isFrequencyForced()
        );
        return selectedOption || available[0];
    }

    changeFrequency(frequency: FrequencyOption): void {
        this.selectedFrequency = frequency;
    }

    private async checkOrModifyFrequency(): Promise<boolean> {
        const initialFrequency =
            this.link.getTxFrequency() < this.link.getRxFrequency() ? this.link.getTxFrequency() : this.link.getRxFrequency();

        const linkSector = this.historyService.logicalTopology.getSector(this.link.getP2mpRepSectorIndex(), this.link.getSectorInDevice());
        let sectorLinkIds = [this.link.getId()];
        if (linkSector) {
            sectorLinkIds = linkSector.getLinks().map((link) => link.getId());
        }

        const modifyCommand = this.historyService
            .command(GtthChangeFrequenciesMultipleLinksCommand)
            .create(initialFrequency, this.link.isFrequencyForced(), sectorLinkIds, linkSector);

        modifyCommand.setNewLinkFrequency(
            this.selectedFrequency.value,
            !this.selectedFrequency.autoOption,
            this.selectedProductsPair.leadingProduct.pdbConfig.fddOffset === PdbFddOffsetEnum.TRUE
        );

        return await this.historyService.doCommand(modifyCommand);
    }
}
